import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, getNt8TradeAlertWebhookUrl } from '../../config';
import { getBearerAuthHeaders } from '../../utils/authFetchHeaders';
import { buildClientSetupPacket, buildNt8ClientMessageBody } from '../../utils/webhookClientPacket';

const fmtDate = (v) => (v ? new Date(v).toLocaleString() : '—');

function CopyBlock({ label, text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(text || ''));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) { /* ignore */ }
  };
  if (!text) return null;
  return (
    <div className="ae-nt8-copy-block">
      <div className="ae-nt8-copy-head">
        <span>{label}</span>
        <button type="button" className="ae-btn ae-btn-ghost" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="ae-nt8-copy-pre">{text}</pre>
    </div>
  );
}

export default function BrandNt8WebhookTokens({ entitySlug, displayName, onNotice, onError }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [keyName, setKeyName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [createdPacket, setCreatedPacket] = useState(null);

  const webhookUrl = getNt8TradeAlertWebhookUrl();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/entities/admin/${encodeURIComponent(entitySlug)}/nt8-webhook-tokens`,
        { headers: getBearerAuthHeaders() },
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load NT8 tokens');
      setKeys(data.keys || []);
    } catch (e) {
      onError?.(e.message);
    } finally {
      setLoading(false);
    }
  }, [entitySlug, onError]);

  useEffect(() => { load(); }, [load]);

  const createToken = async () => {
    setCreating(true);
    setCreatedPacket(null);
    onError?.(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/entities/admin/${encodeURIComponent(entitySlug)}/nt8-webhook-tokens`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
          body: JSON.stringify({
            keyName: keyName.trim() || `${displayName || entitySlug} NT8`,
            userEmail: userEmail.trim() || undefined,
          }),
        },
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to create token');
      const secret = data.key?.token;
      const messageBody = buildNt8ClientMessageBody({ secret });
      setCreatedPacket({
        secret,
        setup: buildClientSetupPacket({ messageBody, webhookUrl }),
        messageBody,
      });
      setKeyName('');
      onNotice?.(`Created NT8 webhook token for ${displayName || entitySlug}. Copy it now — it will not be shown again.`);
      await load();
    } catch (e) {
      onError?.(e.message);
    } finally {
      setCreating(false);
    }
  };

  const deactivate = async (tokenId) => {
    if (!window.confirm('Deactivate this NT8 webhook token? Clients using it will stop connecting.')) return;
    setDeactivatingId(tokenId);
    onError?.(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/entities/admin/${encodeURIComponent(entitySlug)}/nt8-webhook-tokens/${tokenId}`,
        { method: 'DELETE', headers: getBearerAuthHeaders() },
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to deactivate token');
      onNotice?.('NT8 webhook token deactivated.');
      await load();
    } catch (e) {
      onError?.(e.message);
    } finally {
      setDeactivatingId(null);
    }
  };

  const activeKeys = useMemo(() => keys.filter((k) => k.isActive), [keys]);

  return (
    <section className="ae-nt8-tokens">
      <h3 className="ae-stat-group">NT8 webhook ingress</h3>
      <p className="ae-sub">
        One shared NinjaTrader Trade Alert URL for this brand. Trades land under{' '}
        <strong>{displayName || entitySlug}</strong> — map NT8 strategy names in{' '}
        <strong>Admin → Webhooks → Mappings</strong> while this brand is selected.
      </p>

      {loading ? <p className="ae-empty">Loading tokens…</p> : null}

      {!loading && activeKeys.length ? (
        <div className="ae-nt8-token-list">
          <table className="ae-nt8-token-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Owner email</th>
                <th>Secret</th>
                <th>Last used</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className={k.isActive ? '' : 'ae-nt8-token-row--inactive'}>
                  <td>{k.keyName || 'NT8 Webhook'}</td>
                  <td>{k.userEmail || '—'}</td>
                  <td><code>{k.tokenMasked}</code></td>
                  <td>{fmtDate(k.lastUsedAt)}</td>
                  <td>
                    {k.isActive ? (
                      <button
                        type="button"
                        className="ae-btn ae-btn-ghost"
                        onClick={() => deactivate(k.id)}
                        disabled={deactivatingId === k.id}
                      >
                        {deactivatingId === k.id ? '…' : 'Deactivate'}
                      </button>
                    ) : (
                      <span className="ae-nt8-inactive-label">Inactive</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {!loading && !activeKeys.length ? (
        <p className="ae-empty">No active NT8 webhook token for this brand yet.</p>
      ) : null}

      <div className="ae-fields ae-nt8-create-fields">
        <label>
          <span>Token label</span>
          <input
            placeholder={`${displayName || entitySlug} NT8`}
            value={keyName}
            onChange={(ev) => setKeyName(ev.target.value)}
          />
        </label>
        <label>
          <span>Owner email (optional)</span>
          <input
            type="email"
            placeholder="client@example.com"
            value={userEmail}
            onChange={(ev) => setUserEmail(ev.target.value)}
          />
          <small className="ae-field-note">
            Used for webhook log ownership. Defaults to your admin email if blank.
          </small>
        </label>
      </div>

      <button
        type="button"
        className="ae-btn ae-btn-primary"
        onClick={createToken}
        disabled={creating}
      >
        {creating ? 'Creating…' : activeKeys.length ? 'Create another token' : 'Create NT8 webhook token'}
      </button>

      {createdPacket ? (
        <div className="ae-nt8-created">
          <p className="ae-nt8-created-warning" role="status">
            Copy this now — the full secret is only shown once.
          </p>
          <CopyBlock label="Send to client (message body + webhook URL)" text={createdPacket.setup} />
          <CopyBlock label="Webhook URL" text={webhookUrl} />
          <CopyBlock label="Message body (JSON)" text={createdPacket.messageBody} />
        </div>
      ) : null}
    </section>
  );
}
